'use client';

import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import style from './Layout.module.scss';
import ViewFooter from 'ui/component/footer/ViewFooter';
import ViewHeader from 'ui/component/header/ViewHeader';
import useViewData from '@hook/useViewData';
import useViewMode from 'ui/hook/useViewMode';
import { useRecoilState } from 'recoil';
import { viewModeAtom } from '@recoil/viewAtom';
import { useQueryGetCompanyPrivacyPolicyList, useQueryGetCompanyPrivacyPolicy } from '@api/company/privacyPolicy.api';
import { ReadonlyURLSearchParams, usePathname, useSearchParams } from 'next/navigation';
import { useQueryGetDesignMultiLanguageDefault } from '@queries/design';
import useUrlRouter from 'ui/hook/useUrlRouter';
import { Nullable } from 'ui/interface/common/common';
import { LANGUAGE, Language } from 'ui/constants/language';
import { useUrl } from 'ui/hook/useUrl';

const cx = classNames.bind(style);

interface ILayoutProps {
  children: ReactNode;
  className?: string;
  fullWidth?: boolean;
}

const Layout: FC<ILayoutProps> = ({ children, className, fullWidth = false }) => {
  const searchParams = useSearchParams() as ReadonlyURLSearchParams;
  const pathname = usePathname();
  const ref = useRef<HTMLDivElement>(null);
  const { viewMode, handleResize } = useViewMode({ ref });
  const { viewHeader, viewFooter, title: designTitle = '' } = useViewData();
  const { data: privatePolicyList } = useQueryGetCompanyPrivacyPolicyList();
  const languageType = (searchParams.get('languageType') as Language) ?? LANGUAGE.KOR;
  const { data: queryGetDesignMultiLanguageDefaultData } = useQueryGetDesignMultiLanguageDefault();
  const [, setViewModeAtom] = useRecoilState(viewModeAtom);
  const { buildUrl } = useUrl();

  const { useStatus } = queryGetDesignMultiLanguageDefaultData ?? {
    useStatus: 'OFF',
  };
  const { handleUrlClick } = useUrlRouter();

  const [contentMinHeight, setContentMinHeight] = useState(188);

  const footRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const getPathAfterCareer = (fullPath: Nullable<string>) => {
    if (!fullPath) return '';
    const careerPrefix = '/career';
    if (fullPath.startsWith(careerPrefix)) {
      return fullPath.substring(careerPrefix.length);
    }
    return fullPath; // '/career'로 시작하지 않는 경로는 그대로 반환
  };

  const handleLanguageSwitch = async (languageType: Language) => {
    handleUrlClick({
      url: buildUrl(getPathAfterCareer(pathname))
        .withSearchParam('languageType', languageType !== LANGUAGE.KOR ? languageType : null)
        .getResult(),
      isExternal: false,
    });
  };

  useEffect(() => {
    if (viewMode) {
      setViewModeAtom(viewMode);
    }
  }, [viewMode]);

  useEffect(() => {
    handleResize();
  }, []);

  useEffect(() => {
    if (footRef.current && contentRef.current) {
      // 높이 값을 계산해 스크롤이 생기지 않으면서, 화면에 꽉차도록 계산
      // 스크롤이 생기는 경우 : content의 높이 값이 너무 길게 적용되어 스크롤이 생성 된 경우
      // 화면에 꽉차야 하는 경우 : content의 높이 값이 너무 짧게 적용되어 하단 빈 공간이 생기는 경우
      // 참고: https://midasitweb-jira.atlassian.net/browse/ATS-6550
      setContentMinHeight(window.innerHeight - footRef.current.offsetHeight);
    }
  }, [viewFooter, viewMode]);

  return (
    <>
      <div ref={ref} className={cx('contentsArea')}>
        <div className={cx('contentsWrap')}>
          <ViewHeader
            viewMode={viewMode}
            viewHeader={viewHeader}
            options={{
              languageType,
              hasLanguageChangeSwitch: useStatus === 'ON',
              handleLanguageSwitch,
            }}
          />
          <div
            className={cx('container', 'CONTENTS', viewMode, className, { 'full-width': fullWidth })}
            style={{ minHeight: `${contentMinHeight}px` }}
            ref={contentRef}
          >
            {children}
          </div>
          <ViewFooter
            viewMode={viewMode}
            viewFooter={viewFooter}
            policyList={privatePolicyList?.privacyPolicyList ?? []}
            //@ts-ignore
            querySelectedPolicy={useQueryGetCompanyPrivacyPolicy}
            ref={footRef}
            options={{ languageType: languageType }}
          />
        </div>
      </div>
    </>
  );
};

export default Layout;
